import type { ReactNode } from 'react'

interface SectionEyebrowProps {
  label: string
  /** Tailwind bg class for the leading dot (e.g. 'bg-primary', 'bg-success'). */
  dot?: string
  /** Optional inline meta after the hairline (count, "this week", etc). */
  meta?: ReactNode
  /** Optional action node anchored to the right (toolbar, "View all →" link, etc). */
  action?: ReactNode
}

/**
 * SectionEyebrow
 *
 * Canonical section header (White signal v2): a small dot, a 12px/500
 * sentence-case label (no uppercase tracked micro-labels), then a vertical
 * hairline separator, then optional 12px muted meta. Carries an optional right-aligned action slot
 * (toolbar, link, button group).
 *
 * Use this in place of ad-hoc <h2 className="text-xs uppercase…"> headers
 * across the dashboard.
 */
export function SectionEyebrow({ label, dot = 'bg-foreground/30', meta, action }: SectionEyebrowProps) {
  return (
    <div className="flex items-center justify-between gap-3 mb-2.5">
      <div className="flex items-center gap-2 min-w-0">
        <span className={`size-1.5 rounded-full ${dot} shrink-0`} aria-hidden />
        <span className="text-xs font-medium text-foreground leading-4">
          {label}
        </span>
        {meta != null && (
          <>
            <span className="h-3 w-px bg-border/60 shrink-0" aria-hidden />
            <span className="text-xs text-foreground/60 leading-4 truncate">
              {meta}
            </span>
          </>
        )}
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </div>
  )
}
